import { useState, useMemo } from "react";
import { format } from "date-fns";
import { useSwapOffersForMe } from "@/hooks/schedule";
import { SwapOfferDialog } from "./SwapOfferDialog";
import { SwapStatusFilter, getStatusFilterArray, calculateSwapCounts } from "./SwapStatusFilter";
import type { SwapOffer } from "@/hooks/schedule";
import type { SwapFilterStatus } from "./SwapStatusFilter";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Clock, MessageSquare, Loader2, ArrowRightLeft } from "lucide-react";

interface SwapOffersListProps {
  className?: string;
  currentWeek?: Date;
}

export function SwapOffersList({ className, currentWeek }: SwapOffersListProps) {
  const [statusFilter, setStatusFilter] = useState<SwapFilterStatus>('pending');
  const [selectedOffer, setSelectedOffer] = useState<SwapOffer | null>(null);
  const [isOfferDialogOpen, setIsOfferDialogOpen] = useState(false);

  // Helper function to get status-aware badge content
  const getBadgeContent = (status: string) => {
    switch (status) {
      case 'pending':
        return { text: 'New Offer', className: 'text-pink-700 border-pink-300 bg-pink-50' };
      case 'accepted':
        return { text: 'Accepted', className: 'text-green-700 border-green-300 bg-green-50' };
      case 'declined':
        return { text: 'Declined', className: 'text-red-700 border-red-300 bg-red-50' };
      case 'expired':
        return { text: 'Expired', className: 'text-gray-700 border-gray-300 bg-gray-50' };
      default:
        return { text: 'New Offer', className: 'text-pink-700 border-pink-300 bg-pink-50' };
    }
  };

  // Helper function to determine if offer is actionable
  const isOfferActionable = (status: string) => {
    return status === 'pending';
  };
  
  // Get all data for counts calculation
  const { data: allSwapOffers = [], isLoading: isLoadingAll } = useSwapOffersForMe();
  
  // Get filtered data for display
  const statusFilterArray = getStatusFilterArray(statusFilter, 'offers');
  const { data: filteredSwapOffers = [], isLoading: isLoadingFiltered } = useSwapOffersForMe(statusFilterArray);
  
  // Calculate counts for filter badges (SwapOffer uses candidate_status instead of status)
  const counts = useMemo(() => {
    const countsData = {
      pending: 0,
      completed: 0,
      cancelled: 0,
      all: allSwapOffers.length,
    };

    allSwapOffers.forEach((offer) => {
      const status = offer.candidate_status;
      
      if (status === 'pending') {
        countsData.pending++;
      } else if (status === 'accepted') {
        countsData.completed++;
      } else if (status === 'declined' || status === 'expired') {
        countsData.cancelled++;
      }
    });

    return countsData;
  }, [allSwapOffers]);
  
  // Use filtered data for display, all data loading state for initial load
  const swapOffers = filteredSwapOffers;
  const isLoading = isLoadingAll || isLoadingFiltered;

  const formatShiftTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`;
  };

  const formatShiftDate = (startTime: string) => {
    return format(new Date(startTime), 'EEEE, MMM d');
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleViewOffer = (offer: SwapOffer) => {
    setSelectedOffer(offer);
    setIsOfferDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-lg">Swap Offers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading swap offers...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Context-aware empty state message
  const getEmptyStateMessage = () => {
    switch (statusFilter) {
      case 'pending':
        return {
          title: "No pending swap offers",
          subtitle: "When someone wants to swap shifts with you, they'll appear here."
        };
      case 'completed':
        return {
          title: "No completed swaps",
          subtitle: "Completed swaps will appear here once they're finalized."
        };
      case 'cancelled':
        return {
          title: "No cancelled offers",
          subtitle: "Cancelled or declined swap offers will appear here."
        };
      case 'all':
        return {
          title: "No swap offers yet",
          subtitle: "When someone wants to swap shifts with you, they'll appear here."
        };
      default:
        return {
          title: "No swap offers",
          subtitle: "When someone wants to swap shifts with you, they'll appear here."
        };
    }
  };

  if (swapOffers.length === 0) {
    const emptyState = getEmptyStateMessage();
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            Swap Offers
            <Badge variant="secondary" className="text-xs bg-pink-100 text-pink-800">
              {counts.all}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filter Component */}
          <SwapStatusFilter
            selectedStatus={statusFilter}
            onStatusChange={setStatusFilter}
            counts={counts}
          />
          
          {/* Empty State */}
          <div className="text-center py-8">
            <ArrowRightLeft className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {emptyState.title}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {emptyState.subtitle}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            Swap Offers
            <Badge variant="secondary" className="text-xs bg-pink-100 text-pink-800">
              {counts.all}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filter Component */}
          <SwapStatusFilter
            selectedStatus={statusFilter}
            onStatusChange={setStatusFilter}
            counts={counts}
          />
          <ScrollArea className="max-h-[400px] pr-4">
            <div className="space-y-3">
              {swapOffers.map((offer) => {
                const isActionable = isOfferActionable(offer.candidate_status);
                const badgeContent = getBadgeContent(offer.candidate_status);
                
                return (
                  <Card 
                    key={offer.candidate_id} 
                    className={`border-l-4 border-l-pink-500 transition-shadow ${
                      isActionable 
                        ? 'cursor-pointer hover:shadow-md' 
                        : 'opacity-75 cursor-default'
                    }`}
                    onClick={isActionable ? () => handleViewOffer(offer) : undefined}
                  >
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        {/* Header with requester info */}
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-pink-100 text-pink-700 text-xs">
                              {getInitials(offer.requester_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground">
                              {offer.requester_name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              wants to swap shifts
                            </p>
                          </div>
                          <Badge variant="outline" className={badgeContent.className}>
                            {badgeContent.text}
                          </Badge>
                        </div>

                      {/* Shift comparison preview */}
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {/* Their shift */}
                        <div className="space-y-1">
                          <p className="text-muted-foreground">They offer:</p>
                          <div className="bg-blue-50 border border-blue-200 rounded p-2">
                            <p className="font-medium text-blue-900">
                              {formatShiftDate(offer.requester_shift_start)}
                            </p>
                            <div className="flex items-center gap-1 text-blue-700">
                              <Clock className="h-3 w-3" />
                              {formatShiftTime(offer.requester_shift_start, offer.requester_shift_end)}
                            </div>
                          </div>
                        </div>

                        {/* Your shift */}
                        <div className="space-y-1">
                          <p className="text-muted-foreground">For your:</p>
                          <div className="bg-orange-50 border border-orange-200 rounded p-2">
                            <p className="font-medium text-orange-900">
                              {formatShiftDate(offer.my_shift_start)}
                            </p>
                            <div className="flex items-center gap-1 text-orange-700">
                              <Clock className="h-3 w-3" />
                              {formatShiftTime(offer.my_shift_start, offer.my_shift_end)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Message preview */}
                      {offer.message && (
                        <div className="bg-muted/50 rounded-md p-2">
                          <div className="flex items-start gap-2">
                            <MessageSquare className="h-3 w-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-foreground line-clamp-2">
                              {offer.message}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Action hint */}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          Received {format(new Date(offer.created_at), 'MMM d, h:mm a')}
                        </span>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewOffer(offer);
                          }}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Swap Offer Dialog */}
      <SwapOfferDialog
        open={isOfferDialogOpen}
        onOpenChange={setIsOfferDialogOpen}
        swapOffer={selectedOffer}
        currentWeek={currentWeek}
      />
    </>
  );
}
